'use client'

import { styled } from '@rybr/styled-modules'
import React, { ComponentProps, PropsWithChildren, useCallback, useLayoutEffect, useRef } from 'react'

export type ExpansionProps = ComponentProps<typeof Expansion>

export type ExpansionPlusIconProps = Omit<ComponentProps<typeof ExpandIcon>, '$isOpen' | '$color'> & {
  isOpen?: boolean
  color?: string
}

const HEIGHT_STATE = {
  OPEN: 'auto',
  CLOSE: 0
} as const

const Container = styled.div<{ initHeight: number | string }>('ExpansionContainer', ({ initHeight }) => ({
  initHeight
}))

const ExpandIcon = styled.span<{ isOpen?: boolean; color?: string }>('ExpansionPlusIcon', ({ isOpen, color }) => ({
  afterDeg: isOpen ? '0deg' : '-90deg',
  beforeDeg: isOpen ? '0deg' : '-180deg',
  color: color ?? null
}))

export const Expansion: React.FC<
  PropsWithChildren<
    Omit<ComponentProps<typeof Container>, '$initHeight'> & {
      isOpen?: boolean
      minHeight?: string
      maxHeight?: string
      onIsOpenTransitionEnd?: (props: { isOpen: boolean }) => void
    }
  >
> = ({ isOpen, minHeight, maxHeight, className, onIsOpenTransitionEnd, children, ...props }) => {
  const CLOSED_HEIGHT = minHeight ?? HEIGHT_STATE.CLOSE
  const OPEN_HEIGHT = maxHeight ?? HEIGHT_STATE.OPEN

  const ref = useRef<HTMLDivElement>(null)
  const signalRef = useRef(new AbortController())
  const initHeight = useRef<string | number>(isOpen ? OPEN_HEIGHT : CLOSED_HEIGHT)
  const height = useRef<string | number>(initHeight.current)
  const snapshotOpenHeight = useRef<string | number>(initHeight.current)
  const snapshotHeight = useCallback(() => {
    if (ref.current) {
      snapshotOpenHeight.current = `${ref.current.scrollHeight}px`
    }
  }, [ref.current, snapshotOpenHeight.current])

  const setHeight = useCallback(
    (newHeight: number | string) => {
      if (ref.current) {
        height.current = newHeight
        ref.current.style.setProperty('--height', `${newHeight}`)
      }
    },
    [ref.current]
  )

  useLayoutEffect(() => {
    // instantiate the css variable otherwise it can inherit the value from a parent
    setHeight(initHeight.current)
  }, [])

  useLayoutEffect(() => {
    if (!ref.current) {
      return
    }

    // Default open or closed
    if ((isOpen && height.current === OPEN_HEIGHT) || (!isOpen && height.current === CLOSED_HEIGHT)) {
      snapshotHeight()
    }
    // Open triggered
    else if (isOpen) {
      signalRef.current.abort()
      signalRef.current = new AbortController()

      snapshotHeight()

      ref.current.addEventListener(
        'transitionend',
        () => {
          onIsOpenTransitionEnd?.({ isOpen: true })
          setHeight(OPEN_HEIGHT)
        },
        { once: true, signal: signalRef.current.signal }
      )

      window.requestAnimationFrame(() => {
        setHeight(snapshotOpenHeight.current)
      })
    }
    // Close triggered
    else {
      signalRef.current.abort()
      signalRef.current = new AbortController()

      snapshotHeight()

      window.requestAnimationFrame(() => {
        setHeight(snapshotOpenHeight.current)

        window.requestAnimationFrame(() => {
          if (ref.current) {
            ref.current.addEventListener(
              'transitionend',
              () => {
                onIsOpenTransitionEnd?.({ isOpen: false })
              },
              { once: true, signal: signalRef.current.signal }
            )
          }

          setHeight(CLOSED_HEIGHT)
        })
      })
    }
  }, [isOpen, minHeight, maxHeight])

  return (
    <Container
      className={className}
      {...props}
      ref={ref}
      $initHeight={initHeight.current}
    >
      {children}
    </Container>
  )
}

export const ExpansionPlusIcon: React.FC<ExpansionPlusIconProps> = ({ isOpen, color, ...props }) => {
  return (
    <ExpandIcon
      $isOpen={isOpen}
      $color={color}
      {...props}
    />
  )
}

export const ExpansionIcon: React.FC<{ isOpen: boolean; icon: React.ReactElement<{ className?: string }> }> = ({
  isOpen,
  icon
}) => {
  return React.cloneElement(icon, {
    className: `${icon.props.className ?? ''} ${isOpen ? 'Rotate0' : 'Rotate180'}`
  })
}
